import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toast } from 'material-react-toastify';
import { SchedulePersonnel } from '../../interfaces/Schedule';
import {
  UnavailableDaysEntry,
  UnavailableDaysPreview,
} from '../../interfaces/UnavailableDays';
import { parseUnavailableDays } from '../../services/UnavailableDays';
import { toastApiError } from '../../utils/ApiError';
import { formatSingleDay } from '../../utils/Date';
import { mergeUnavailableDays } from '../../utils/UnavailableDays';
import PtoSubtypeDropdown from './PtoSubtypeDropdown';

interface UnavailableDaysAssistantProps {
  defaultExpanded?: boolean;
  departmentSectionID: number;
  personnels: SchedulePersonnel[];
  startAt: Date;
  endAt: Date;
  onApply: (entries: UnavailableDaysEntry[]) => void;
}

const UnavailableDaysAssistant = (props: UnavailableDaysAssistantProps) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [image, setImage] = useState<{ name: string; dataUrl: string }>();
  const [preview, setPreview] = useState<UnavailableDaysPreview>();
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [readingImage, setReadingImage] = useState(false);
  const [inputError, setInputError] = useState('');
  const controller = useRef<AbortController>();
  const fileReader = useRef<FileReader>();

  useEffect(
    () => () => {
      controller.current?.abort();
      fileReader.current?.abort();
    },
    [],
  );

  const handleImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    setPreview(undefined);
    setImage(undefined);
    if (
      !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
      file.size === 0 ||
      file.size > 5 * 1024 * 1024
    ) {
      setInputError(t('schedule.unavailable_assistant.image_error') as string);
      return;
    }
    setInputError('');
    setReadingImage(true);
    const reader = new FileReader();
    fileReader.current = reader;
    reader.onload = () => {
      setImage({ name: file.name, dataUrl: reader.result as string });
      setReadingImage(false);
    };
    reader.onerror = () => {
      setInputError(t('schedule.unavailable_assistant.image_error') as string);
      setReadingImage(false);
    };
    reader.readAsDataURL(file);
  };

  const handleParse = async () => {
    const requestController = new AbortController();
    controller.current = requestController;
    setLoading(true);
    setPreview(undefined);
    try {
      const result = await parseUnavailableDays(
        props.departmentSectionID,
        props.personnels.map((personnel) => personnel.ID),
        props.startAt,
        props.endAt,
        prompt,
        image?.dataUrl,
        requestController.signal,
      );
      if (!requestController.signal.aborted) {
        setPreview(result);
        setSelectedRows(result.entries.map((_, index) => index));
      }
    } catch (error) {
      if (!requestController.signal.aborted) toastApiError(error);
    } finally {
      if (!requestController.signal.aborted) setLoading(false);
    }
  };

  const selectedEntries = (preview?.entries ?? []).filter((_, index) =>
    selectedRows.includes(index),
  );
  const { conflicts } = mergeUnavailableDays(props.personnels, selectedEntries);

  return (
    <Accordion defaultExpanded={props.defaultExpanded} sx={{ mt: 2, mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('schedule.unavailable_assistant.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant='body2' sx={{ mb: 2 }}>
          {t('schedule.unavailable_assistant.description')}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          label={t('schedule.unavailable_assistant.prompt')}
          placeholder={
            t('schedule.unavailable_assistant.placeholder') as string
          }
          value={prompt}
          disabled={loading}
          inputProps={{ maxLength: 12000 }}
          onChange={(event) => {
            setPrompt(event.target.value);
            setPreview(undefined);
          }}
        />
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexWrap: 'wrap',
            my: 2,
          }}
        >
          <Button
            component='label'
            variant='outlined'
            disabled={loading || readingImage}
          >
            {t('schedule.unavailable_assistant.upload')}
            <input
              hidden
              type='file'
              accept='image/png,image/jpeg,image/webp'
              onChange={handleImage}
              disabled={loading || readingImage}
            />
          </Button>
          {image && (
            <>
              <Typography variant='body2'>{image.name}</Typography>
              <Button
                disabled={loading}
                onClick={() => {
                  setImage(undefined);
                  setPreview(undefined);
                }}
              >
                {t('schedule.unavailable_assistant.remove_image')}
              </Button>
            </>
          )}
          <Button
            variant='contained'
            disabled={
              loading ||
              readingImage ||
              (!prompt.trim() && !image) ||
              !props.personnels.length ||
              props.startAt > props.endAt
            }
            onClick={handleParse}
            startIcon={
              loading ? (
                <CircularProgress size={16} color='inherit' />
              ) : undefined
            }
          >
            {t(
              loading
                ? 'schedule.unavailable_assistant.reading'
                : 'schedule.unavailable_assistant.preview',
            )}
          </Button>
        </Box>
        <Typography variant='caption' color='text.secondary'>
          {t('schedule.unavailable_assistant.data_notice')}
        </Typography>
        {inputError && (
          <Alert severity='error' sx={{ mt: 1 }}>
            {inputError}
          </Alert>
        )}
        {preview && (
          <Box sx={{ mt: 2 }}>
            {preview.warnings.map((warning, index) => (
              <Alert key={index} severity='warning' sx={{ mb: 1 }}>
                {warning}
              </Alert>
            ))}
            {!preview.entries.length ? (
              <Alert severity='info'>
                {t('schedule.unavailable_assistant.empty')}
              </Alert>
            ) : (
              <>
                <Typography variant='body2' sx={{ mb: 1 }}>
                  {t('schedule.unavailable_assistant.review')}
                </Typography>
                <TableContainer sx={{ maxHeight: 360 }}>
                  <Table size='small' stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          {t('schedule.unavailable_assistant.include')}
                        </TableCell>
                        <TableCell>{t('personnel.name')}</TableCell>
                        <TableCell>{t('shared.start_at')}</TableCell>
                        <TableCell>{t('shared.end_at')}</TableCell>
                        <TableCell>{t('pto_subtype.title')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.entries.map((entry, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Checkbox
                              checked={selectedRows.includes(index)}
                              inputProps={{
                                'aria-label': t(
                                  'schedule.unavailable_assistant.include_row',
                                  { row: index + 1 },
                                ) as string,
                              }}
                              onChange={(_, checked) =>
                                setSelectedRows(
                                  checked
                                    ? [...selectedRows, index]
                                    : selectedRows.filter(
                                        (row) => row !== index,
                                      ),
                                )
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {
                              props.personnels.find(
                                (personnel) =>
                                  personnel.ID === entry.personnelID,
                              )?.name
                            }
                          </TableCell>
                          <TableCell>
                            {formatSingleDay(t, entry.startAt)}
                          </TableCell>
                          <TableCell>
                            {formatSingleDay(t, entry.endAt)}
                          </TableCell>
                          <TableCell>
                            <PtoSubtypeDropdown
                              value={entry.ptoSubtype}
                              onChange={(ptoSubtype) =>
                                setPreview({
                                  ...preview,
                                  entries: preview.entries.map(
                                    (row, rowIndex) =>
                                      rowIndex === index
                                        ? { ...row, ptoSubtype }
                                        : row,
                                  ),
                                })
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {conflicts.length > 0 && (
                  <Alert severity='error' sx={{ mt: 1 }}>
                    {t('schedule.unavailable_assistant.conflicts', {
                      names: conflicts.join(', '),
                    })}
                  </Alert>
                )}
                <Button
                  variant='contained'
                  sx={{ mt: 2 }}
                  disabled={!selectedEntries.length || conflicts.length > 0}
                  onClick={() => {
                    props.onApply(selectedEntries);
                    setPreview(undefined);
                    toast.success(t('schedule.unavailable_assistant.applied'));
                  }}
                >
                  {t('schedule.unavailable_assistant.apply', {
                    count: selectedEntries.length,
                  })}
                </Button>
              </>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default UnavailableDaysAssistant;
